import { useState } from "react";
import { openExternal } from "../discord/handshake";
import { useT } from "../i18n/context";
import { intlLocale } from "../i18n/index";
import { LEGAL } from "../routes/myData";
import { useStore } from "../store";
import { usePrivacy } from "./usePrivacy";

const NOTICE_KEY = "hcc:legal-notice";

function seenNotice(): boolean {
  try {
    return localStorage.getItem(NOTICE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * The home screen's two notes (review 0016): a waiting deletion with its date and the way to cancel
 * (decision 88), and once per device "by playing you accept the terms" with both texts (decision 91).
 */
export function HomeNotices({ onMyData }: { onMyData: () => void }) {
  const { t, err, locale } = useT();
  const sdk = useStore((s) => s.sdk);
  const lastError = useStore((s) => s.lastError);
  const set = useStore((s) => s.set);
  const due = usePrivacy().status?.deletionDueAt ?? null;
  const [noticed, setNoticed] = useState(seenNotice);
  const dismiss = () => {
    try {
      localStorage.setItem(NOTICE_KEY, "1");
    } catch {
      // Without storage the note comes back next time; nothing breaks.
    }
    setNoticed(true);
  };
  const link = "font-bold underline underline-offset-2";
  return (
    <>
      {lastError !== null && (
        // Why you are back here: the table you asked for would not have you (review 0027 D7).
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-[16px] border-[1.5px] border-danger-soft/70 bg-danger/25 px-4 py-2.5 text-[14px]"
        >
          <span>{err(lastError)}</span>
          <button
            type="button"
            aria-label={t("legal.dismiss")}
            className="h-8 w-8 flex-none rounded-full text-[18px] leading-none"
            onClick={() => set({ lastError: null })}
          >
            ×
          </button>
        </div>
      )}
      {due !== null && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-[16px] border-[1.5px] border-danger-soft/70 bg-danger/25 px-4 py-3 text-[14px]"
        >
          <span>
            {t("data.delete.pending", {
              date: new Date(due).toLocaleDateString(intlLocale(locale), { dateStyle: "long" }),
            })}
          </span>
          <button
            type="button"
            className="btn-glass h-10 rounded-pill px-4 font-bold"
            onClick={onMyData}
          >
            {t("data.delete.cancel")}
          </button>
        </div>
      )}
      {!noticed && (
        <div className="flex items-center justify-between gap-3 rounded-[16px] border-[1.5px] border-edge bg-panel-strong px-4 py-2.5 text-[13px]">
          <span>
            {t("legal.notice")}{" "}
            <button
              type="button"
              className={link}
              onClick={() => void openExternal(sdk, LEGAL.terms)}
            >
              {t("legal.terms")}
            </button>
            {" · "}
            <button
              type="button"
              className={link}
              onClick={() => void openExternal(sdk, LEGAL.privacy)}
            >
              {t("legal.privacy")}
            </button>
          </span>
          <button
            type="button"
            aria-label={t("legal.dismiss")}
            className="h-8 w-8 flex-none rounded-full text-[18px] leading-none"
            onClick={dismiss}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}
